import { useContext, useMemo, useState } from 'react'
import { recordDuplicateReason } from 'src/api/customer'
import { ENV } from 'src/config/env'
import { useAppContext } from 'src/helpers/app'
import { QuizQuestionContext, SCREEN_QUIZ_QUESTION } from '../../contextAPI'
import { PATH_URL, REDIRECT_PATH, getQueryParameters } from 'src/helpers'
import { useHistory } from 'react-router-dom'

const REASON = [
  { id: 'looking_proposal', label: 'I’m looking for my proposal', isSelected: false },
  { id: 'different_colors', label: 'I want to see different colors', isSelected: false },
  { id: 'schedule_new_event', label: 'I want to schedule a new event', isSelected: false },
]

const getPortalLoginUrl = (emailAddress?: string, proposalUrl?: string) => {
  const baseUrl = ENV.CUSTOMER_PORTAL_FRONTEND_URL.replace(/\/$/, '')
  const portalUrl = baseUrl || new URL(REDIRECT_PATH.LOGIN).origin
  const url = new URL('/login', portalUrl)

  if (proposalUrl) {
    try {
      const proposalDestination = new URL(proposalUrl)
      url.pathname = proposalDestination.pathname
      proposalDestination.searchParams.forEach((value, key) => {
        if (!['token', 'stytch_token_type', 'email'].includes(key)) url.searchParams.set(key, value)
      })
    } catch {}
  }

  const quizParams = getQueryParameters()
  quizParams.forEach((value, key) => {
    if (!['token', 'stytch_token_type', 'email'].includes(key)) url.searchParams.set(key, value)
  })
  if (emailAddress) url.searchParams.set('email', emailAddress)
  return url.toString()
}

const getStoredEmailAddress = () => {
  try {
    const emailAddress = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')?.customerInfo?.emailAddress
    return typeof emailAddress === 'string' ? emailAddress.trim() || undefined : undefined
  } catch {
    return undefined
  }
}

export const useMetBefore = () => {
  const history = useHistory()
  const { setQuizScreen, recordResponse } = useContext(QuizQuestionContext)
  const { sessionID } = useAppContext()
  const [reason, setReason] = useState(REASON)
  const [additionalNotes, setAdditionalNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleNext = async () => {
    if (isSubmitting) return

    const selectedReasons = reason.filter((item) => item.isSelected)
    const payload = {
      sessionId: sessionID,
      reason: selectedReasons.map((item) => item.label),
      additionalNotes,
      proposalId: recordResponse?.proposalId ?? recordResponse?.proposal?.proposalId,
    }

    if (selectedReasons.some((item) => item.id === 'looking_proposal')) {
      setIsSubmitting(true)
      await recordDuplicateReason(payload).catch((error) => console.error('Failed to record duplicate reason', error))
      window.location.href = getPortalLoginUrl(getStoredEmailAddress(), recordResponse?.proposalUrl)
      setIsSubmitting(false)
      return
    }

    setQuizScreen(SCREEN_QUIZ_QUESTION.END_QUIZ)
    const searchParams = getQueryParameters()
    history.push({ pathname: PATH_URL.QUIZ_END, search: searchParams.toString() })
    recordDuplicateReason(payload)
  }

  const handleSelectedReason = (reasonId: string) => {
    setReason((pre) => {
      const newState = [...pre]
      const index = newState.findIndex((s) => s.id === reasonId)
      if (index !== -1) newState[index] = { ...newState[index], isSelected: !newState[index].isSelected }
      return newState
    })
  }

  const nextDisabled = useMemo(() => isSubmitting || reason.every((r) => !r.isSelected), [isSubmitting, reason])

  return {
    handleNext,
    reason,
    handleSelectedReason,
    additionalNotes,
    setAdditionalNotes,
    nextDisabled,
  }
}
